import { Item } from "../../../../pojo/Item";
import { Report } from "../../../../pojo/Report";
import { NotificationService } from "../../../../services/notification.service";
import { ReportService } from "../../../../services/report.service";
import { SearchService } from "../../../../services/search/search.service";
import { S25Util } from "../../../../util/s25-util";
import { ObjectSearchFilter } from "./se.object.search.filter.component";
import { SeSearchUtil } from "./se.object.search.util";

export const EVENT_EXPORT_REPORT_ID = -253;
export const REPORT_LIMIT = 1500;

export type EventSearchExportOptions = {
    filter: ObjectSearchFilter;
    userContactId?: number;
};

export type EventSearchExportSource = EventSearchExportOptions & {
    isLoading: boolean;
    isLoggedIn: boolean;
    resultCount: number;
};

export class SeEventSearchExportService {
    static assertExportReady(source: EventSearchExportSource): boolean {
        if (!source.isLoggedIn) {
            return false;
        }
        if (source.isLoading) {
            S25Util.showError(
                "There is no data to process yet. If a search is running, please wait for it to complete.",
            );
            return false;
        }
        if (!source.resultCount) {
            S25Util.showError(
                "There is no data to process yet. If a search is running, please wait for it to complete.",
            );
            return false;
        }
        if (source.resultCount > REPORT_LIMIT) {
            S25Util.showError(
                `This search contains too many items. Please restrict your search to ${REPORT_LIMIT} or fewer items, or save the search and run the report from the reports page.`,
            );
            return false;
        }
        return true;
    }

    static async exportEventSearchResults(source: EventSearchExportSource): Promise<boolean> {
        if (!SeEventSearchExportService.assertExportReady(source)) {
            return false;
        }

        const report: Report.SimpleObject = { rpt_id: EVENT_EXPORT_REPORT_ID, rpt_engine: "WS", isAsync: false };
        const searchModel = SeSearchUtil.buildEventSearchModel(source.filter, {
            userContactId: source.userContactId,
        });
        const dateRange = SeSearchUtil.resolveEventReportDateRange(source.filter);
        let queryId: number | undefined;
        try {
            queryId = await SearchService.getTempSearch(Item.Ids.Event, S25Util.deepCopy(searchModel));
            const context: Report.Context = {
                ev_query_id: queryId,
                date_parm1: dateRange.startDate,
                date_parm2: dateRange.endDate,
            };
            NotificationService.post("Report will be downloaded upon completion");
            const reportResult = await ReportService.runReportRequest({
                report: report,
                context: context,
                format: null,
                delivery: null,
                otherRecipients: null,
                otherMessage: null,
                query: null,
                selectGroupBy: true,
            });
            return reportResult !== false;
        } finally {
            if (queryId) {
                await SearchService.deleteSearch(queryId, Item.Ids.Event);
            }
        }
    }
}
